import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaDownload, FaExpand, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useEffect, useState } from 'react';

const PDFViewer = ({ sample, onClose }) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const editionBadge = sample.edition === 'micro'
    ? { bg: 'bg-accent-blue', icon: '🌈', label: 'Micro Edition' }
    : { bg: 'bg-accent-orange', icon: '🚀', label: 'Macro Edition' };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/90 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative w-full max-w-7xl max-h-[95vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className={`${editionBadge.bg} text-white px-6 py-4 flex items-center justify-between`}>
            <div className="flex items-center gap-3">
              <span className="text-4xl">{editionBadge.icon}</span>
              <div>
                <h3 className="text-2xl font-display font-bold">
                  {sample.title}
                </h3>
                <p className="text-sm text-white/80">
                  {editionBadge.label} • {sample.uploadDate && new Date(sample.uploadDate).toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Download Button */}
              <motion.a
                href={sample.pdfUrl}
                download={sample.fileName || sample.title}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="p-3 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                title="Download PDF"
              >
                <FaDownload className="text-xl" />
              </motion.a>

              {/* Fullscreen Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.open(sample.pdfUrl, '_blank')}
                className="p-3 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                title="Open in new tab"
              >
                <FaExpand className="text-xl" />
              </motion.button>

              {/* Close Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onClose}
                className="p-3 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                title="Close (ESC)"
              >
                <FaTimes className="text-xl" />
              </motion.button>
            </div>
          </div>

          {/* PDF Viewer */}
          <div className="flex-1 bg-neutral-gray overflow-auto relative">
            {sample.pdfUrl ? (
              <iframe
                src={`${sample.pdfUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                className="w-full h-full min-h-[700px]"
                title={sample.title}
                frameBorder="0"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="text-9xl mb-6">📄</div>
                  <h3 className="text-3xl font-display font-bold text-primary-green-dark mb-3">
                    No PDF Available
                  </h3>
                  <p className="text-xl text-neutral-gray-dark mb-6">
                    This sample hasn't been uploaded yet.
                  </p>
                  <motion.button
                    onClick={onClose}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="btn-primary"
                  >
                    Close
                  </motion.button>
                </div>
              </div>
            )}
          </div>

          {/* Footer with Subscribe CTA */}
          <div className="bg-gradient-to-r from-primary-green to-primary-green-light text-white px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <p className="font-display font-bold text-lg">
                Enjoying this sample? 🎉
              </p>
              <p className="text-white/90">
                Subscribe to get fresh content like this every week!
              </p>
            </div>
            <motion.a
              href="/subscribe"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-primary-green px-8 py-3 rounded-button font-display font-bold text-lg shadow-card whitespace-nowrap"
            >
              Subscribe Now 🚀
            </motion.a>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default PDFViewer;